// Exactly-trimmed block audio for the dubbing player.
//
// The problem this solves: a block's audio must stop at its `endTime` to the
// millisecond, and almost every block IS cut — the transcript slot covers only
// the spoken part while the synthesized audio carries pauses on top (measured:
// 85-96% of each file falls outside the slot). Stopping a playing `<audio>` at
// the right instant cannot do that: polling overshoots when the main thread is
// busy, and `pause()` throws away audio already rendered but not yet heard, so
// the cut lands a device-dependent amount early. Both vary run to run.
//
// So the trim becomes a property of the DATA, not of the timing: hand the
// element a file whose length already IS the played window and let it stop by
// itself. `ended` then fires exactly on the sample the block should end on.
//
// Two caches, because the two costs are wildly different (measured on a 45.7s
// source): decoding is ~200ms and encoding the PCM body ~17ms, but re-cutting
// that body to a different length is a byte-range slice — 0.2-0.6ms, constant
// regardless of length. Keeping them apart is what lets a live timestamp edit
// re-cut without a hitch.
//
//   L1  url               -> PCM body (Blob)     expensive, capped by bytes
//   L2  url + cutSec      -> WAV clip object URL cheap, capped by count
//
// The decoded AudioBuffer is released as soon as the body exists: it is float32
// and roughly twice the size of the int16 body, and it sits on the JS heap where
// a Blob does not.

import {
  applyFadeOut,
  buildWavHeader,
  bytesPerFrame,
  encodePcm16,
  framesForSeconds,
  usableChannelCount,
} from '@src/services/wavEncoder';
import { getSharedAudioContext } from '@src/services/audioContext';

// Hard guards, mirroring how `services/audioWaveform.js` refuses oversized
// sources.
//
// The byte cap is the one that actually protects memory, because it is checked
// BEFORE `decodeAudioData` — the decode is the big allocation (an uncapped one
// on a 90-minute video is what used to kill the tab with SIGILL). Sizing it:
// a block's text is capped at MAX_CHARACTER_PER_BLOCK_SRT (1000) which is about
// 90 seconds of speech, and measured block audio runs ~117kbps, so a legitimate
// worst case is ~1.3MB. 4MB leaves 3x headroom and still bounds one decode at
// roughly 4MB / 117kbps ≈ 280s ≈ 54MB of float32 PCM — two orders of magnitude
// below the allocation that failed.
const MAX_SOURCE_BYTES = 4 * 1024 * 1024;

// Checked AFTER the decode, so it does NOT guard the decode itself — the byte
// cap above does that. This one keeps an unexpectedly long source out of the
// cache and off the encode, whatever its bitrate.
const MAX_SOURCE_SECONDS = 5 * 60;

// Body length kept beyond what the current cut needs, so lengthening a block by
// a small drag re-cuts from cache instead of decoding again.
const BODY_HEADROOM_SEC = 5;

// L1 is bounded by total bytes rather than entry count: bodies differ in size by
// an order of magnitude, so a count would bound nothing useful.
const MAX_BODY_CACHE_BYTES = 50 * 1024 * 1024;
// L2 entries are small (one clip each) but each holds an object URL, so the cap
// is about not leaking URLs rather than about memory.
const MAX_CLIP_CACHE_ENTRIES = 16;

// Decoding is the only expensive step and it competes with the waveform and
// filmstrip work already running; the same bound they use.
const MAX_CONCURRENT_BUILDS = 3;

// Ramp to silence at the cut. Inside the played window, so the clip still ends
// exactly on `endTime`.
const FADE_OUT_SEC = 0.003;

// Cut lengths are quantised to whole milliseconds so the L2 key is stable —
// block timestamps carry millisecond precision anyway (HH:mm:ss,SSS).
const CUT_PRECISION_MS = 1000;

const bodyCache = new Map();
const clipCache = new Map();
const pendingBodies = new Map();
const pendingClips = new Map();

const waitingBuilds = [];
let runningBuilds = 0;
let cachedBodyBytes = 0;

const roundCutSec = (cutSec) =>
  Math.round(cutSec * CUT_PRECISION_MS) / CUT_PRECISION_MS;

const clipKey = (url, cutSec) => `${url}|${cutSec}`;

// --- cache eviction -------------------------------------------------------

const dropBody = (url) => {
  const entry = bodyCache.get(url);
  if (!entry) return;
  cachedBodyBytes -= entry.body.size;
  bodyCache.delete(url);
};

const evictBodies = () => {
  while (cachedBodyBytes > MAX_BODY_CACHE_BYTES && bodyCache.size > 1) {
    dropBody(bodyCache.keys().next().value);
  }
};

const dropClip = (key) => {
  const entry = clipCache.get(key);
  if (!entry) return;
  URL.revokeObjectURL(entry.objectUrl);
  clipCache.delete(key);
};

const evictClips = () => {
  while (clipCache.size > MAX_CLIP_CACHE_ENTRIES) {
    dropClip(clipCache.keys().next().value);
  }
};

// Re-insert to move an entry to the most-recently-used end of the Map.
const touch = (cache, key) => {
  const entry = cache.get(key);
  if (entry === undefined) return undefined;
  cache.delete(key);
  cache.set(key, entry);
  return entry;
};

// --- L1: decode once, keep the PCM body -----------------------------------

const pumpBuilds = () => {
  const onSettled = () => {
    runningBuilds -= 1;
    pumpBuilds();
  };
  while (runningBuilds < MAX_CONCURRENT_BUILDS && waitingBuilds.length) {
    runningBuilds += 1;
    waitingBuilds.shift()().then(onSettled, onSettled);
  }
};

const runBounded = (task) =>
  new Promise((resolve) => {
    waitingBuilds.push(() => {
      const settled = task().then(resolve, () => resolve(null));
      return settled;
    });
    pumpBuilds();
  });

// A plain fetch reuses the HTTP cache, but `<audio>` loads the same url without
// an Origin header (the fallback player still does), which can leave S3 holding
// a copy with no `Access-Control-Allow-Origin` — reusing that copy fails CORS.
// Retry once bypassing the cache, which always gets the Origin-varied response.
const fetchSource = async (url) => {
  try {
    return await fetch(url);
  } catch (error) {
    return fetch(url, { cache: 'reload' });
  }
};

const buildBody = async (url, neededSeconds) => {
  const context = getSharedAudioContext();
  if (!context) return null;

  const response = await fetchSource(url);
  if (!response.ok) return null;
  if (Number(response.headers.get('content-length')) > MAX_SOURCE_BYTES) {
    return null;
  }
  const encoded = await response.arrayBuffer();
  if (encoded.byteLength > MAX_SOURCE_BYTES) return null;

  const audioBuffer = await context.decodeAudioData(encoded);
  if (audioBuffer.duration > MAX_SOURCE_SECONDS) return null;

  const { sampleRate, length: sourceFrames } = audioBuffer;
  const channelCount = usableChannelCount(audioBuffer);
  const frameCount = framesForSeconds(
    neededSeconds + BODY_HEADROOM_SEC,
    sampleRate,
    sourceFrames,
  );
  // The AudioBuffer goes out of scope here — only the (half as large, off-heap)
  // Blob survives.
  const body = new Blob([encodePcm16(audioBuffer, frameCount, channelCount)]);
  return { body, sampleRate, channelCount, frameCount, sourceFrames };
};

// Whether a cached body already holds enough frames for `neededSeconds` — true
// whenever it covers the whole source, since nothing longer exists.
const bodyCovers = (entry, neededSeconds) =>
  entry.frameCount >= entry.sourceFrames ||
  entry.frameCount >= framesForSeconds(neededSeconds, entry.sampleRate);

const ensureBody = async (url, neededSeconds) => {
  const cached = touch(bodyCache, url);
  if (cached && bodyCovers(cached, neededSeconds)) return cached;
  if (cached) dropBody(url);

  const pending = pendingBodies.get(url);
  if (pending) {
    const entry = await pending;
    if (entry && bodyCovers(entry, neededSeconds)) return entry;
  }

  const build = runBounded(() => buildBody(url, neededSeconds)).then(
    (entry) => {
      pendingBodies.delete(url);
      if (entry) {
        bodyCache.set(url, entry);
        cachedBodyBytes += entry.body.size;
        evictBodies();
      }
      return entry;
    },
  );
  pendingBodies.set(url, build);
  return build;
};

// --- L2: cut the body to length -------------------------------------------

// Header + body prefix + faded tail. `Blob.slice` is a view, not a copy, so this
// stays O(1) in the clip length; only the few hundred bytes of the fade are read
// back and rewritten.
const buildClip = async (entry, frames) => {
  const { body, sampleRate, channelCount } = entry;
  const frameBytes = bytesPerFrame(channelCount);
  const fadeFrames = Math.min(
    framesForSeconds(FADE_OUT_SEC, sampleRate),
    frames,
  );
  const plainFrames = frames - fadeFrames;

  const header = buildWavHeader({
    sampleRate,
    channelCount,
    frameCount: frames,
  });
  const parts = [header, body.slice(0, plainFrames * frameBytes)];
  if (fadeFrames > 0) {
    const tail = await body
      .slice(plainFrames * frameBytes, frames * frameBytes)
      .arrayBuffer();
    parts.push(applyFadeOut(tail, channelCount));
  }
  return new Blob(parts, { type: 'audio/wav' });
};

/**
 * An object URL for `url`'s audio cut to exactly `cutSec` seconds, or null when
 * the source cannot be prepared (no Web Audio, CORS, unsupported codec, over the
 * size caps) — callers then fall back to playing the original url.
 *
 * The returned URL is owned by this module's cache; do not revoke it.
 *
 * @param {string} url - Block audio url
 * @param {number} cutSec - Played window in seconds, i.e. MIN(slot, duration)
 * @returns {Promise<string | null>}
 */
export const getTrimmedAudioUrl = async (url, cutSec) => {
  if (!url || !(cutSec > 0)) return null;
  const cut = roundCutSec(cutSec);
  const key = clipKey(url, cut);

  const cached = touch(clipCache, key);
  if (cached) return cached.objectUrl;
  const pending = pendingClips.get(key);
  if (pending) return pending;

  const build = (async () => {
    try {
      const entry = await ensureBody(url, cut);
      if (!entry) return null;
      const frames = framesForSeconds(cut, entry.sampleRate, entry.frameCount);
      if (frames <= 0) return null;
      const objectUrl = URL.createObjectURL(await buildClip(entry, frames));
      clipCache.set(key, { objectUrl });
      evictClips();
      return objectUrl;
    } catch (error) {
      return null;
    } finally {
      pendingClips.delete(key);
    }
  })();
  pendingClips.set(key, build);
  return build;
};

/**
 * Warm the caches for a clip that is about to play, without waiting for it.
 * Entering a block should never pay the decode.
 */
export const prefetchTrimmedAudio = (url, cutSec) => {
  getTrimmedAudioUrl(url, cutSec).catch(() => {});
};

/** Drop every cached body and clip, revoking the object URLs. */
export const resetTrimmedAudioCache = () => {
  Array.from(clipCache.keys()).forEach(dropClip);
  bodyCache.clear();
  pendingBodies.clear();
  pendingClips.clear();
  cachedBodyBytes = 0;
};

export default getTrimmedAudioUrl;
